/* Floating back-to-top + live support entry (snippets/samduo-float-service.liquid) */
(() => {
  if (customElements.get('samduo-float-service')) return;

  const SCROLL_IDLE = 300; // ms without scroll events before the stack shows again
  const SHEET_TRANSITION = 300;
  const AVOID_GAP = 16;
  // Fixed bottom bars the stack must sit above while they are shown (theme.js animates them via inline styles)
  const AVOID_SELECTOR = '.product-sticky-form__card';
  // Must match the media query that enables the hover card in samduo-float-service.css
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SamduoFloatService extends HTMLElement {
    constructor() {
      super();
      this.idleTimer = 0;
      this.closeTimer = 0;
      this.avoidFrame = 0;
      this.avoidObserver = null;
      this.savedOverflow = '';
      this.scheduleAvoid = this.scheduleAvoid.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onTopClick = this.onTopClick.bind(this);
      this.onAgentClick = this.onAgentClick.bind(this);
      this.onSheetClick = this.onSheetClick.bind(this);
      this.onSheetCancel = this.onSheetCancel.bind(this);
      this.onSheetClose = this.onSheetClose.bind(this);
      this.onHoverChange = this.onHoverChange.bind(this);
    }

    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold || '400', 10);
      this.topButton = this.querySelector('[data-sd-float-top]');
      this.agentButton = this.querySelector('[data-sd-float-agent]');
      const sheetId = this.agentButton && this.agentButton.getAttribute('aria-controls');
      this.sheet = sheetId ? document.getElementById(sheetId) : null;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      hoverQuery.addEventListener('change', this.onHoverChange);
      if (this.topButton) this.topButton.addEventListener('click', this.onTopClick);
      if (this.agentButton) this.agentButton.addEventListener('click', this.onAgentClick);
      if (this.sheet) {
        this.sheet.addEventListener('click', this.onSheetClick);
        this.sheet.addEventListener('cancel', this.onSheetCancel);
        this.sheet.addEventListener('close', this.onSheetClose);
      }

      this.avoidEls = Array.from(document.querySelectorAll(AVOID_SELECTOR));
      if (this.avoidEls.length) {
        this.avoidObserver = new MutationObserver(this.scheduleAvoid);
        this.avoidEls.forEach((el) => this.avoidObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'] }));
        window.addEventListener('resize', this.scheduleAvoid);
        this.scheduleAvoid();
      }

      this.updateThreshold();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      hoverQuery.removeEventListener('change', this.onHoverChange);
      if (this.topButton) this.topButton.removeEventListener('click', this.onTopClick);
      if (this.agentButton) this.agentButton.removeEventListener('click', this.onAgentClick);
      if (this.sheet) {
        this.sheet.removeEventListener('click', this.onSheetClick);
        this.sheet.removeEventListener('cancel', this.onSheetCancel);
        this.sheet.removeEventListener('close', this.onSheetClose);
      }
      if (this.avoidObserver) {
        this.avoidObserver.disconnect();
        window.removeEventListener('resize', this.scheduleAvoid);
      }
      cancelAnimationFrame(this.avoidFrame);
      clearTimeout(this.idleTimer);
      clearTimeout(this.closeTimer);
    }

    scheduleAvoid() {
      if (this.avoidFrame) return;
      this.avoidFrame = requestAnimationFrame(() => {
        this.avoidFrame = 0;
        this.updateAvoid();
      });
    }

    updateAvoid() {
      const own = this.getBoundingClientRect();
      let lift = 0;
      this.avoidEls.forEach((el) => {
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        const rect = el.getBoundingClientRect();
        if (!rect.height || rect.right <= own.left || rect.left >= own.right) return;
        lift = Math.max(lift, window.innerHeight - rect.top + AVOID_GAP);
      });
      this.style.setProperty('--sd-float-avoid', `${Math.round(lift)}px`);
    }

    updateThreshold() {
      this.classList.toggle('is-past-threshold', window.scrollY > this.threshold);
    }

    onScroll() {
      this.updateThreshold();
      if (this.sheet && this.sheet.open) return;

      this.classList.add('is-scrolling');
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.classList.remove('is-scrolling'), SCROLL_IDLE);
    }

    onTopClick() {
      window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    onAgentClick() {
      // Desktop shows the card on hover / focus via CSS; only touch devices use the sheet.
      if (hoverQuery.matches || !this.sheet) return;
      this.openSheet();
    }

    onHoverChange(event) {
      if (event.matches) this.closeSheet(true);
    }

    openSheet() {
      if (this.sheet.open) return;
      clearTimeout(this.closeTimer);
      clearTimeout(this.idleTimer);
      this.classList.remove('is-scrolling');

      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.sheet.showModal();
      this.agentButton.setAttribute('aria-expanded', 'true');
      void this.sheet.offsetWidth; // commit the off-screen state so the slide-up transition runs
      this.sheet.classList.add('is-open');
    }

    closeSheet(immediate = false) {
      if (!this.sheet || !this.sheet.open) return;
      this.sheet.classList.remove('is-open');
      clearTimeout(this.closeTimer);
      if (immediate || reducedMotion.matches) {
        this.sheet.close();
        return;
      }
      this.closeTimer = setTimeout(() => this.sheet.close(), SHEET_TRANSITION);
    }

    onSheetClick(event) {
      // A click on the ::backdrop is reported with the <dialog> itself as the target.
      if (event.target === this.sheet || event.target.closest('[data-sd-float-close]')) {
        this.closeSheet();
      }
    }

    onSheetCancel(event) {
      event.preventDefault();
      this.closeSheet();
    }

    onSheetClose() {
      clearTimeout(this.closeTimer);
      this.sheet.classList.remove('is-open');
      document.documentElement.style.overflow = this.savedOverflow;
      if (this.agentButton) this.agentButton.setAttribute('aria-expanded', 'false');
    }
  }

  customElements.define('samduo-float-service', SamduoFloatService);
})();
